import { series, seriesType, seriesRenderOrder, yAxisRange, seriesColumn, seriesColumnOverlapped } from '../types.js';
import { getColumnRenderGroups } from '../column/getColumnRenderGroups.js';
import { getColumnStackedRenderGroups } from '../column-stacked/getColumnStackedRenderGroups.js';
import { getLineRenderGroups } from '../line-area/line/getLineRenderGroups.js';
import { RenderGroup, renderGroupsMap, seriesTypeFlatDataset } from './types.js';
import { getColumnOverlappedRenderGroups } from '../column-overlapped/getColumnOverlappedRenderGroups.js';
import { getAreaRenderGroups } from '../line-area/area/getAreaRenderGroups.js';
import { KeyValueSet } from '../axis/key/KeyValueSet.js';
export const getRenderGroups = (series: series[], keyValueSet: KeyValueSet, axisLeftRange?: yAxisRange, axisRightRange?: yAxisRange): renderGroupsMap => {
  if (isYAxisRangesInvalid(axisLeftRange, axisRightRange)) {
    return {
      left: [],
      right: [],
      all: [],
      preDomain: [],
      postDomain: []
    };
  }
  let renderGroups: RenderGroup[] = [];
  for (const dataset of toFlattenedDatasetsBasedOnType(series, keyValueSet)) {
    renderGroups.push(...getRenderGroupsBasedOnDataset(dataset, series));
  }
  renderGroups = renderGroups.filter(x => x.minValue != null && x.maxValue != null).sort((a, b) => seriesRenderOrder.indexOf(a.type) - seriesRenderOrder.indexOf(b.type));
  const isPreDomain = (x: RenderGroup) => x.type === 'column' || x.type === 'column-overlapped' || x.type === 'column-stacked';
  return {
    left: renderGroups.filter(x => (x.yTarget ?? 'left') === 'left'),
    right: renderGroups.filter(x => (x.yTarget ?? 'left') === 'right'),
    all: renderGroups,
    preDomain: renderGroups.filter(x => isPreDomain(x)),
    postDomain: renderGroups.filter(x => !isPreDomain(x))
  };
};
const isYAxisRangesInvalid = (axisLeftRange?: yAxisRange, axisRightRange?: yAxisRange) => axisLeftRange?.min != null && axisLeftRange?.max != null && axisLeftRange.min >= axisLeftRange.max || axisRightRange?.min != null && axisRightRange?.max != null && axisRightRange.min >= axisRightRange.max;
const getRenderGroupsBasedOnDataset = (dataset: seriesTypeFlatDataset, series: series[]) => {
  switch (dataset[0].series.type) {
    case 'column':
      return getColumnRenderGroups(dataset as seriesTypeFlatDataset<seriesColumn>, series);
    case 'column-stacked':
      return getColumnStackedRenderGroups(dataset);
    case 'column-overlapped':
      return getColumnOverlappedRenderGroups(dataset as seriesTypeFlatDataset<seriesColumnOverlapped>, series);
    case 'line':
      return getLineRenderGroups(dataset);
    case 'area':
      return getAreaRenderGroups(dataset);
    default:
      throw new Error("Unknown type for a series detected, can't retrieve render groups...");
  }
};
const toFlattenedDatasetsBasedOnType = (series: series[], keyValueSet: KeyValueSet): seriesTypeFlatDataset[] => {
  const datasetMap = {} as Record<seriesType, seriesTypeFlatDataset>;
  for (const _series of series) {
    const dataset = datasetMap[_series.type] ?? (datasetMap[_series.type] = []);
    for (const datapoint of _series.data) {
      if (!keyValueSet.hasKey(datapoint.key)) {
        continue;
      }
      dataset.push({
        series: _series,
        datapoint
      });
    }
  }
  return Object.values(datasetMap).filter(x => x.length);
};